import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import MobileHeader from "./MobileHeader";
import AdminMandatoryNotifications from "../../modules/settings/notifications/AdminMandatoryNotifications";
import MaintenanceLockOverlay from "../../modules/settings/notifications/MaintenanceLockOverlay";
import QuickCreateSheet from "./QuickCreateSheet";
import MobileSwipeNavigation from "./MobileSwipeNavigation";
import { getMobileNavLayout, WORK_FULL_LIST_PATH } from "./mobileNavConfig";

function resolveActiveTab(pathname, tabs) {
  if (
    pathname.startsWith("/m/work") ||
    pathname.startsWith(WORK_FULL_LIST_PATH)
  ) {
    return tabs.some((t) => t.key === "work") ? "work" : null;
  }
  if (pathname.startsWith("/m/crm")) {
    return tabs.some((t) => t.key === "crm") ? "crm" : null;
  }
  if (pathname.startsWith("/m/finance")) {
    return tabs.some((t) => t.key === "finance") ? "finance" : null;
  }
  if (pathname.startsWith("/m/elearning")) {
    return tabs.some((t) => t.key === "elearning") ? "elearning" : null;
  }
  if (pathname.startsWith("/m/hrm")) {
    return tabs.some((t) => t.key === "hrm") ? "hrm" : null;
  }
  if (
    pathname.startsWith("/m/favorites") ||
    pathname.startsWith("/m/settings") ||
    pathname.startsWith("/m/profile")
  ) {
    return "more";
  }
  const matched = tabs.find((tab) => {
    if (tab.path) return pathname.startsWith(tab.path);
    return pathname.startsWith(`/m/${tab.key}`);
  });
  return matched?.key || "home";
}

export default function MobileMainLayout({ user, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [fabOpen, setFabOpen] = useState(false);

  const resolvedUser = useMemo(() => {
    if (user && typeof user === "object" && (user.id || user.email || user.name)) {
      return user;
    }
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [user]);

  const { showFab, tabs } = useMemo(() => getMobileNavLayout(), []);

  const currentTab =
    resolveActiveTab(location.pathname, tabs) ||
    (location.pathname.startsWith("/m/home") ? "home" : "more");

  useEffect(() => {
    document.documentElement.classList.add("mobile-shell-active");
    return () => {
      document.documentElement.classList.remove("mobile-shell-active");
    };
  }, []);

  const fabIndex = showFab ? Math.floor(tabs.length / 2) : -1;
  const leftTabs = fabIndex >= 0 ? tabs.slice(0, fabIndex) : tabs;
  const rightTabs = fabIndex >= 0 ? tabs.slice(fabIndex) : [];

  const handleTabClick = (tab) => {
    navigate(tab.path || `/m/${tab.key}`);
  };

  const renderTabButton = (tab) => {
    const Icon = tab.icon;
    const isActive = currentTab === tab.key;
    return (
      <button
        key={tab.key}
        type="button"
        onClick={() => handleTabClick(tab)}
        className={`mobile-bottom-nav__item transition-colors ${
          isActive ? "text-red-700" : "text-gray-400"
        }`}
      >
        <Icon size={22} strokeWidth={isActive ? 2.5 : 2} className="shrink-0" />
        <span
          className={`max-w-full truncate text-[10px] leading-tight ${
            isActive ? "font-bold" : "font-medium"
          }`}
        >
          {tab.label}
        </span>
      </button>
    );
  };

  return (
    <div className="mobile-app-shell">
      <AdminMandatoryNotifications />
      <MaintenanceLockOverlay />
      <div className="mobile-app-header">
        <MobileHeader user={resolvedUser || {}} />
      </div>
      <MobileSwipeNavigation>
        <main className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain overscroll-x-none">
          {children ?? <Outlet />}
        </main>
      </MobileSwipeNavigation>

      <nav id="bottom-nav" className="mobile-bottom-nav z-50 shadow-[0_-1px_8px_rgba(15,23,42,0.06)]">
        <div className="mobile-bottom-nav__bar px-1">
          {leftTabs.map(renderTabButton)}

          {showFab ? (
            <div className="mobile-bottom-nav__fab">
              <button
                type="button"
                onClick={() => setFabOpen(true)}
                aria-label="Tạo nhanh"
                className="flex h-14 w-14 items-center justify-center rounded-full bg-red-700 text-white shadow-lg shadow-red-200 ring-4 ring-white transition-transform active:scale-95"
              >
                <Plus size={28} strokeWidth={2.5} />
              </button>
            </div>
          ) : null}

          {rightTabs.map(renderTabButton)}
        </div>
      </nav>

      <QuickCreateSheet open={fabOpen} onClose={() => setFabOpen(false)} />
    </div>
  );
}
